import { useNavigate } from "@tanstack/react-router";
import { AlertTriangleIcon } from "lucide-react";
import { useCallback, useState } from "react";

import { Button } from "~/components/ui/button";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";

// Agents is a BETA surface — it can drive real apps, the browser, and the host, so we
// require an explicit opt-in before anyone can open the flow. Consent is stored per-device
// in localStorage (no backend/migration needed for the beta gate); clearing this key or
// switching device re-shows the agreement. Upgrade path: record consent per-account on the
// server if we later need an auditable acceptance.
const CONSENT_KEY = "morrow.agents-beta-consent";

function readConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(CONSENT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Per-device beta-tester consent for the Agents surface. */
export function useAgentsBetaConsent(): { consented: boolean; accept: () => void } {
  const [consented, setConsented] = useState<boolean>(readConsent);
  const accept = useCallback(() => {
    try {
      window.localStorage.setItem(CONSENT_KEY, "1");
    } catch {
      // ignore — still let them through this session even if storage is blocked
    }
    setConsented(true);
  }, []);
  return { consented, accept };
}

/**
 * Gates the whole Agents subtree. Until the visitor accepts the beta-tester agreement, the
 * agent flow is NOT rendered and a non-dismissible modal is shown: "Yes" opts in and reveals
 * the flow; "No" navigates away so they never enter it. Placed at the /agents route layout so
 * every entry point (sidebar, command palette, deep-link) is covered.
 */
export function AgentsBetaGate({ children }: { children: React.ReactNode }) {
  const { consented, accept } = useAgentsBetaConsent();
  const navigate = useNavigate();

  if (consented) return <>{children}</>;

  return (
    // Strictly gated: no backdrop/esc dismiss — the only ways out are the two buttons
    // (Yes reveals the flow, No navigates away). onOpenChange is a no-op so stray close
    // attempts are ignored.
    <AlertDialog open onOpenChange={() => undefined}>
      <AlertDialogPopup>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangleIcon className="size-5 text-warning" aria-hidden />
            Agents are in beta
          </AlertDialogTitle>
          <AlertDialogDescription>
            Agents is an experimental feature we're actively testing. An agent can take real actions
            on your behalf — running your connected apps, driving the browser, and more — so it can
            behave unexpectedly and, in some cases, do things that are hard to undo.
            <br />
            <br />
            By continuing you agree to take part as a beta tester: you understand it's incomplete,
            may make mistakes, and you'll use it with that in mind. You can stop any time.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => void navigate({ to: "/" })}>
            No, take me back
          </Button>
          <Button onClick={accept}>Yes, I'll be a beta tester</Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}
